import { getCurrentScope, onScopeDispose, readonly, ref, watchEffect } from 'vue'

import { handleErrorMessage } from './error.helpers'

import { useErrorStore } from '@/stores/error'

/**
 * Runs `method` whenever its reactive dependencies change. Starting a new request aborts the one
 * still in flight, so a slow older response can never overwrite the result of a newer one. Pass
 * the `signal` on to the request to cancel it on the network as well.
 */
export const useApi = <T>(method: (signal: AbortSignal) => Promise<T>) => {
  const data = ref<T>()
  const isLoading = ref<boolean>(true)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()
  let controller: AbortController | null = null

  const fetchData = async () => {
    controller?.abort()
    const { signal } = (controller = new AbortController())

    isLoading.value = true
    error.value = null
    try {
      const result = await method(signal)
      if (!signal.aborted) {
        data.value = result
      }
    } catch (e) {
      if (!signal.aborted) {
        error.value = e as Error
        errorStore.setError(handleErrorMessage(e))
      }
    } finally {
      if (!signal.aborted) {
        isLoading.value = false
      }
    }
  }

  watchEffect(() => void fetchData())

  if (getCurrentScope()) {
    onScopeDispose(() => controller?.abort())
  }

  return {
    data: readonly(data),
    isLoading: readonly(isLoading),
    error: readonly(error),
    fetchData,
  }
}
